/**
 * RasterLoadingOverlay — follow-up AK-383 (founder UAT 2026-07-07).
 *
 * Founder feedback после #438: спиннер смены даты «где-то спрятан» (жил на
 * полосе дат SatelliteStrip и в углу мини-карты поля — незаметно). Просьба —
 * «давай в центр экрана». Этот компонент рисует ЗАМЕТНЫЙ индикатор по центру
 * карты: полупрозрачная плашка (`bg-popover`) + Loader2 + текст «Загружаю
 * снимок…».
 *
 * UX-контракт (CLAUDE.md §3 «нет толкай-и-жди»):
 *   - плашка центрирована абсолютно внутри ближайшего `relative`-родителя
 *     (map wrapper) — видна сразу, поверх карты;
 *   - карту НЕ блокирует: `pointer-events-none` на слое-обёртке,
 *     `pointer-events-auto` только на самой плашке — панорамирование/зум под
 *     ней продолжают работать;
 *   - `role="status"` + `aria-live="polite"` — скринридер объявит загрузку;
 *   - `prefers-reduced-motion`: спиннер остаётся (лёгкая допустимая анимация),
 *     без агрессивных движений плашки — она просто появляется/исчезает.
 *
 * Переиспользуется на обеих поверхностях: /map (v2.tsx, поверх map-canvas) и
 * мини-карта карточки поля (FieldOverviewTab). Сигнал загрузки берётся из
 * общего хука `useRasterTilesLoading` — единый механизм фидбэка.
 */
import { Loader2 } from 'lucide-react';

interface RasterLoadingOverlayProps {
  /** true → показать индикатор. Обычно = useRasterTilesLoading(...). */
  loading: boolean;
  /** Подпись под спиннером. */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

export function RasterLoadingOverlay({
  loading,
  label = 'Загружаю снимок…',
  className,
  'data-testid': testId = 'raster-loading-overlay',
}: RasterLoadingOverlayProps) {
  if (!loading) return null;

  return (
    // Слой-обёртка растянут на весь родитель, но НЕ ловит указатель — карта под
    // ним остаётся интерактивной (pointer-events только на плашке ниже).
    <div
      className={
        'pointer-events-none absolute inset-0 z-30 flex items-center justify-center' +
        (className ? ` ${className}` : '')
      }
      aria-hidden={false}
    >
      <div
        role="status"
        aria-live="polite"
        data-testid={testId}
        className="pointer-events-auto flex items-center gap-3 rounded-lg border border-border bg-popover/95 px-4 py-3 text-sm font-medium text-popover-foreground shadow-lg backdrop-blur-sm"
      >
        <Loader2 size={18} className="animate-spin text-accent-brand" aria-hidden />
        <span>{label}</span>
      </div>
    </div>
  );
}
